import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, CheckCircle2 } from 'lucide-react';
import {
  CountdownUnits,
} from '@/lib/countdown-utils';
import { supabase } from '@/integrations/supabase/client';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

// ── Inline countdown hook ────────────────────────────────────────────────────
function useCountdown(targetDateStr: string | null): CountdownUnits {
  const [units, setUnits] = useState<CountdownUnits>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!targetDateStr) return;
    const target = new Date(targetDateStr).getTime();

    function tick() {
      const diff = Math.max(0, target - Date.now());
      setUnits({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    }

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDateStr]);

  return units;
}

// ── Nav links ────────────────────────────────────────────────────────────────
const navLinks = [
  { label: 'התחברו', href: '/login' },
  { label: 'איך זה עובד?', href: '#how-it-works' },
  { label: 'שאלות ותשובות', href: '#faq' },
  { label: 'צור קשר', href: 'https://wa.me/972546339018?text=%D7%94%D7%99%D7%99%20%D7%90%D7%99%D7%99%D7%9C%2C%20%D7%99%D7%A9%20%D7%9C%D7%99%20%D7%A9%D7%90%D7%9C%D7%94%20%D7%91-Bracket%20Run%3A%20', external: true },
];

// ── LandingPage ──────────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [groupCount, setGroupCount] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Bracket deadline: April 18 2026, 20:00 Israel time (UTC+3)
  const targetDate = '2026-04-18T17:00:00Z';
  const { days, hours, minutes, seconds } = useCountdown(targetDate);
  const isExpired = Date.now() >= new Date(targetDate).getTime();

  useEffect(() => {
    supabase
      .from('groups')
      .select('*', { count: 'exact', head: true })
      .then(({ count }) => {
        if (count !== null && count >= 3) setGroupCount(count);
      });
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith('#')) {
      const element = document.getElementById(href.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Pad number to 2 digits
  const pad = (n: number) => String(n).padStart(2, '0');

  const timerUnits = [
    { value: pad(days), label: 'ימים' },
    { value: pad(hours), label: 'שעות' },
    { value: pad(minutes), label: 'דקות' },
    { value: pad(seconds), label: 'שניות' },
  ];

  return (
    <div className="min-h-screen bg-[#15191E] text-[#FEFEFE] overflow-x-hidden font-leon" dir="rtl">

      {/* ── Landing Header ────────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#15191E]/80 backdrop-blur-md border-b border-white/5">
        <div className="flex items-center h-16 px-4 max-w-6xl mx-auto gap-4">

          {/* Hamburger */}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="p-2 text-white/70 hover:text-white transition-colors flex-shrink-0"
            aria-label="תפריט"
          >
            {menuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
          </button>

          <div className="flex-1" />

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img src="/images/logo.png" alt="Bracket Run" className="h-10 w-auto" />
          </Link>
        </div>

        {/* Drawer */}
        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="border-t border-white/5 bg-[#15191E]/95 backdrop-blur-md px-4 py-4 flex flex-col gap-1"
            >
              {navLinks.map(({ label, href, external }) =>
                external ? (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                    className="text-white/70 hover:text-[#F97116] transition-colors py-3 text-base border-b border-white/5 last:border-0"
                  >
                    {label}
                  </a>
                ) : (
                  <a
                    key={label}
                    href={href}
                    onClick={(e) => {
                      if (href.startsWith('#')) {
                        e.preventDefault();
                        handleNavClick(href);
                      } else {
                        setMenuOpen(false);
                        navigate(href);
                      }
                    }}
                    className="text-white/70 hover:text-[#F97116] transition-colors py-3 text-base border-b border-white/5 last:border-0 cursor-pointer"
                  >
                    {label}
                  </a>
                )
              )}
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      {/* ── Hero Section ──────────────────────────────────────────────────── */}
      <main className="pt-16 px-4 pb-6">
        <div className="relative overflow-hidden rounded-3xl min-h-[85vh] flex flex-col justify-center">

          {/* Video background */}
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            src="/videos/basketball.mp4"
          />

          {/* ── Basketball decorative elements ── */}

          {/* Small basketball — bottom-right */}
          <motion.div
            className="absolute bottom-[60px] right-[-30px] pointer-events-none z-[5]"
            animate={{ rotate: -360, y: [0, 10, 0] }}
            transition={{
              rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
              y: { duration: 3.5, repeat: Infinity, ease: 'easeInOut' },
            }}
            style={{ opacity: 0.18 }}
          >
            <svg width="140" height="140" viewBox="0 0 200 200" fill="none">
              <circle cx="100" cy="100" r="96" stroke="#F97116" strokeWidth="2" fill="rgba(249,113,22,0.04)" />
              <path d="M 4,100 Q 50,58 100,100 Q 150,142 196,100" stroke="#F97116" strokeWidth="1.8" fill="none" />
              <path d="M 100,4 Q 58,50 100,100 Q 142,150 100,196" stroke="#F97116" strokeWidth="1.8" fill="none" />
            </svg>
          </motion.div>

          {/* Court 3-point arc — bottom */}
          <motion.div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none z-[5]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.8, delay: 0.6 }}
          >
            <svg width="520" height="280" viewBox="0 0 520 280" fill="none">
              <path d="M 20,280 A 240,240 0 0,1 500,280" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" fill="none" />
              <path d="M 80,280 A 180,180 0 0,1 440,280" stroke="rgba(255,255,255,0.07)" strokeWidth="1" fill="none" />
            </svg>
          </motion.div>

          {/* Hoop silhouette — top-right */}

          {/* Hero content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="relative z-10 w-full px-6 pb-12 flex flex-col items-center text-center"
          >
            {!isExpired && (
              <motion.div variants={fadeUp} className="mb-8 inline-block bg-white/5 backdrop-blur-md rounded-3xl p-4 sm:p-8 shadow-2xl">
                <div className="flex items-center justify-center gap-4 sm:gap-8" dir="ltr">
                  {timerUnits.map(({ value, label }) => (
                    <div key={label} className="flex flex-col items-center leading-none">
                      <span className="text-[#FEFEFE] font-black text-4xl sm:text-5xl md:text-6xl tabular-nums tracking-tight drop-shadow-md">
                        {value}
                      </span>
                      <span className="text-white/60 text-sm sm:text-base mt-2 font-medium">{label}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tight text-[#FEFEFE] leading-[1.1] mb-4 max-w-3xl"
            >
              {isExpired ? 'הפלייאוף הסתיים.' : 'השעון מתקתק. הפלייאוף מתקרב.'}
            </motion.h1>

            {isExpired && (
              <motion.p
                variants={fadeUp}
                className="text-white/60 text-sm sm:text-base mb-4"
              >
                נתראה בעונה הבאה
              </motion.p>
            )}

            <motion.p
              variants={fadeUp}
              className="text-white text-base sm:text-xl leading-relaxed mb-8 max-w-xl mx-auto"
            >
              משחקים מול חברים, מי מצליח לנחש את התוצאות וצובר יותר נקודות
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col items-center w-full max-w-sm">
              <motion.button
                onClick={() => navigate('/login')}
                className="w-full bg-[#F97116] text-white font-bold text-lg rounded-full py-4 px-10 transition-all active:scale-95"
                style={{ boxShadow: '0 0 32px rgba(249,113,22,0.4)' }}
                whileHover={{ scale: 1.03, boxShadow: '0 0 48px rgba(249,113,22,0.6)' }}
                whileTap={{ scale: 0.97 }}
              >
                התחילו עכשיו וצרפו חברים
              </motion.button>

              <p className="mt-4 text-white/50 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#F97116]" />
                חינם לגמרי | הרשמה מהירה
              </p>
            </motion.div>
          </motion.div>
        </div>
      </main>

      {/* ── App Sneak Peek ────────────────────────────────────────────────── */}
      <section className="py-24 relative bg-[#15191E]">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black tracking-tight">
              תראו איך זה נראה מבפנים
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white text-lg max-w-xl mx-auto">
              בנינו חווית משתמש ששמה את האקשן במרכז. בלי אקסלים, בלי טבלאות נסתרות - הכל קורה כאן.
            </motion.p>

            <motion.div variants={fadeUp} className="w-full max-w-md mx-auto mt-8">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img src="/images/phone-mockup.png" alt="Bracket Run app" className="w-full h-auto block" />
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-10">
              <motion.button
                onClick={() => navigate('/login')}
                className="bg-[#F97116] text-white font-bold text-lg rounded-full py-4 px-10 transition-all active:scale-95"
                style={{ boxShadow: '0 0 32px rgba(249,113,22,0.4)' }}
                whileHover={{ scale: 1.03, boxShadow: '0 0 48px rgba(249,113,22,0.6)' }}
                whileTap={{ scale: 0.97 }}
              >
                התחילו עכשיו וצרפו חברים
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 bg-[#1A1F25] relative">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black mb-4">
              איך מתחילים לשחק ב-3 צעדים?
            </motion.h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={{ visible: { transition: { staggerChildren: 0.18 } } }}
          >
            {[
              {
                num: '1',
                title: 'פותחים ליגה',
                body: 'מזמינים את החברים, הקולגות מהעבודה או המשפחה לליגה הפרטית שלכם ומתחילים את הטראש-טוק.',
                offset: '',
                img: '/images/step_1.jpg',
              },
              {
                num: '2',
                title: 'מנחשים',
                body: 'נחשו תוצאות סדרות מדויקות, והימורי צד על סטטיסטיקות בכל סיבוב. הקפידו למלא את כל ההימורים הפתוחים בכל שלב!',
                offset: 'md:translate-y-8',
                img: '/images/step_2.jpg',
              },
              {
                num: '3',
                title: 'צוברים נקודות',
                body: 'המשחקים משוחקים, התוצאות מתעדכנות לייב, ואתם רואים איך אתם מטפסים בטבלה.',
                offset: '',
                img: '/images/step_3.jpg',
              },
            ].map(({ num, title, body, offset, img }) => (
              <motion.div
                key={num}
                variants={{
                  hidden: { opacity: 0, y: 40, scale: 0.95 },
                  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 260, damping: 22 } },
                }}
                className={`bg-[#15191E] border border-white/5 rounded-3xl overflow-hidden flex flex-col ${offset}`}
              >
                <picture>
                  <source srcSet={img.replace(/\.jpg$/, '.webp')} type="image/webp" />
                  <img src={img} alt={`שלב ${num}`} className="w-full h-48 object-cover" />
                </picture>
                <div className="p-8 text-center flex flex-col items-center">
                  <h3 className="text-2xl font-bold mb-3 text-white">{title}</h3>
                  <p className="text-white leading-relaxed">{body}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-center mt-14"
          >
            <motion.button
              onClick={() => navigate('/login')}
              className="bg-[#F97116] text-white font-bold text-lg rounded-full py-4 px-10 transition-all active:scale-95"
              style={{ boxShadow: '0 0 32px rgba(249,113,22,0.4)' }}
              whileHover={{ scale: 1.03, boxShadow: '0 0 48px rgba(249,113,22,0.6)' }}
              whileTap={{ scale: 0.97 }}
            >
              התחילו עכשיו וצרפו חברים
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── Benefits ──────────────────────────────────────────────────────── */}
      <section className="py-24 bg-[#F97116]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black mb-4 text-white">
              האדרנלין של הפלייאוף הולך לקבל בוסט
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white text-lg max-w-2xl mx-auto">
              כל החברים שלכם בוואטסאפ מתווכחים מי תיקח אליפות? בואו נודה באמת, זה לא כיף עד שאין הוכחות.
            </motion.p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="p-6 sm:p-8 rounded-3xl bg-black/10 flex gap-5 hover:bg-black/15 transition-colors"
            >
              <div>
                <h3 className="text-xl font-bold mb-2 text-white">הכל קורה בלייב</h3>
                <p className="text-white/80 leading-relaxed">עדכוני תוצאות וחישוב נקודות מהיר ללא גליונות אקסל מעצבנים שמצריכים תואר בראיית חשבון.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 sm:p-8 rounded-3xl bg-black/10 flex gap-5 hover:bg-black/15 transition-colors"
            >
              <div>
                <h3 className="text-xl font-bold mb-2 text-white">סגירת ההימור לאחר שהתחיל</h3>
                <p className="text-white/80 leading-relaxed">כל ההימורים ננעלים דקה לפני הכדור הראשון, כך שאין תירוצים או שינויים אחרי הג'אמפ בול.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6 sm:p-8 rounded-3xl bg-black/10 flex gap-5 hover:bg-black/15 transition-colors"
            >
              <div>
                <h3 className="text-xl font-bold mb-2 text-white">טבלת מובילים אישית</h3>
                <p className="text-white/80 leading-relaxed">טבלת Leaderboard חיה שמתעדכנת ומראה לכולם מי הווינר האמיתי של החבורה.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="p-6 sm:p-8 rounded-3xl bg-black/10 flex gap-5 hover:bg-black/15 transition-colors"
            >
              <div>
                <h3 className="text-xl font-bold mb-2 text-white">הצצה לניחושים של כולם</h3>
                <p className="text-white/80 leading-relaxed">אחרי סגירת השעון, אפשר לראות את הבחירות של שאר החברים, לתכנן אסטרטגיה ולעשות קצת טראש טוק.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Video Section ──────────────────────────────────────────────────── */}
      <section className="py-6 px-4 bg-[#15191E]">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="max-w-6xl mx-auto rounded-3xl overflow-hidden"
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-auto block"
            src="/videos/basketball_old.mp4"
          />
        </motion.div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section id="faq" className="py-24 bg-[#1A1F25]">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-4">שאלות נפוצות</h2>
          </motion.div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-3xl px-8 py-2 border-none">
              <AccordionTrigger className="text-xl text-black font-bold hover:no-underline hover:text-[#F97116] gap-4 text-right justify-end flex-row-reverse">
                מה זה Bracket Run?
              </AccordionTrigger>
              <AccordionContent className="text-black/75 text-lg leading-relaxed text-right">
                תחרות ניחושים לפלייאוף ה-NBA בין חברים. מנחשים תוצאות סדרות, ממלאים ברקט, ומתחרים על המקום הראשון בלידרבורד.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white rounded-3xl px-8 py-2 border-none">
              <AccordionTrigger className="text-xl text-black font-bold hover:no-underline hover:text-[#F97116] gap-4 text-right justify-end flex-row-reverse">
                כמה זה עולה?
              </AccordionTrigger>
              <AccordionContent className="text-black/75 text-lg leading-relaxed text-right">
                חינם לגמרי. אין מנוי, אין פרסומות.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white rounded-3xl px-8 py-2 border-none">
              <AccordionTrigger className="text-xl text-black font-bold hover:no-underline hover:text-[#F97116] gap-4 text-right justify-end flex-row-reverse">
                איך ההימורים עובדים?
              </AccordionTrigger>
              <AccordionContent className="text-black/75 text-lg leading-relaxed text-right">
                בוחרים מנצח + תוצאה מדויקת (למשל "תאנדר 4-2"). ככל שהתוצאה פחות צפויה לפי הסיכויים, יותר נקודות.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white rounded-3xl px-8 py-2 border-none">
              <AccordionTrigger className="text-xl text-black font-bold hover:no-underline hover:text-[#F97116] gap-4 text-right justify-end flex-row-reverse">
                מה זה Global Bracket?
              </AccordionTrigger>
              <AccordionContent className="text-black/75 text-lg leading-relaxed text-right">
                ניחוש של כל הפלייאוף מראש, 15 סדרות מסיבוב 1 ועד הגמר. שיטת ניקוד נפרדת עם טבלה גלובלית לכל המשתתפים.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white rounded-3xl px-8 py-2 border-none">
              <AccordionTrigger className="text-xl text-black font-bold hover:no-underline hover:text-[#F97116] gap-4 text-right justify-end flex-row-reverse">
                מה זה קומישינר?
              </AccordionTrigger>
              <AccordionContent className="text-black/75 text-lg leading-relaxed text-right">
                מנהל הקבוצה. יכול להזמין ולהסיר חברים, לערוך הימורים, ולשלוח תזכורות.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <Link to="/faq" className="text-[#F97116] hover:text-[#F97116]/80 transition-colors text-lg font-bold">
              עוד שאלות ותשובות &larr;
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden" style={{ backgroundImage: "url('/images/new_texture.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        {/* Background glow / image placeholder */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl h-[500px] bg-[#F97116]/20 blur-[120px] rounded-full pointer-events-none"></div>
        <img src="/images/bottom-cta-bg.png" alt="" className="absolute left-0 bottom-0 max-h-[120%] opacity-30 pointer-events-none hidden md:block" onError={(e) => e.currentTarget.style.display = 'none'} />

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl md:text-6xl font-black mb-6"
          >
            מוכנים לזריקת הפתיחה?
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white mb-10 max-w-2xl mx-auto"
          >
            הפלייאוף תכף מתחיל. פתחו קבוצה עכשיו לפני שהכל ננעל ותתחילו להזמין את החברים באמת.
          </motion.p>

          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            onClick={() => navigate('/login')}
            className="bg-white text-[#F97116] font-bold text-xl rounded-full py-5 px-12 transition-all active:scale-95"
            whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(249,113,22,0.8)' }}
            whileTap={{ scale: 0.95 }}
          >
            פתחו קבוצה בחינם 🏀
          </motion.button>
        </div>
      </section>


    </div>
  );
};

export default LandingPage;
